import React, { useId, useState } from 'react';
import { CheckCircle2, ShieldCheck, XCircle } from 'lucide-react';

import {
  describeFreeTextValidation,
  normalizeFreeTextValidation,
  validateFreeTextAnswer,
} from '../../utils/freeTextValidation';

/**
 * Validation Rules for a Free Text item (FECOA-2056).
 *
 * Edits a form-friendly draft (numbers as typed, modes as select values) and
 * converts to and from the rule stored in the item's options.validation. The
 * rule itself, its default messages and the answer checks come from
 * utils/freeTextValidation, the same rules the participant sees on the web,
 * the tablet and the phone.
 */

export const EMPTY_FREE_TEXT_VALIDATION_DRAFT = Object.freeze({
  format: 'any',
  allowDecimals: true,
  maxDecimalPlaces: '',
  minMode: 'none',
  minValue: '',
  maxMode: 'none',
  maxValue: '',
  allowSpaces: false,
  allowedCharacters: '',
  minLength: '',
  requireLetter: false,
  requireNumber: false,
  errorMessage: '',
});

const FORMAT_OPTIONS = [
  { value: 'any', label: 'Any characters (no restriction)' },
  { value: 'number', label: 'A number' },
  { value: 'letters', label: 'Letters only' },
  { value: 'alphanumeric', label: 'Letters and numbers only' },
  { value: 'digits', label: 'Numbers only (digits 0-9, e.g. a code)' },
];

const CHARACTER_FORMATS = ['letters', 'alphanumeric', 'digits'];
const REQUIRED_CHARACTER_FORMATS = ['any', 'alphanumeric'];

/** Stored rule -> editable draft. */
export function freeTextValidationToDraft(validation, maxLength) {
  const { rule } = normalizeFreeTextValidation(validation, maxLength);
  if (!rule) return { ...EMPTY_FREE_TEXT_VALIDATION_DRAFT };
  return {
    ...EMPTY_FREE_TEXT_VALIDATION_DRAFT,
    format: rule.format,
    allowDecimals: rule.allowDecimals !== false,
    maxDecimalPlaces: rule.maxDecimalPlaces != null ? String(rule.maxDecimalPlaces) : '',
    minMode: 'min' in rule ? (rule.minInclusive === false ? 'gt' : 'gte') : 'none',
    minValue: 'min' in rule ? String(rule.min) : '',
    maxMode: 'max' in rule ? (rule.maxInclusive === false ? 'lt' : 'lte') : 'none',
    maxValue: 'max' in rule ? String(rule.max) : '',
    allowSpaces: Boolean(rule.allowSpaces),
    allowedCharacters: rule.allowedCharacters || '',
    minLength: rule.minLength != null ? String(rule.minLength) : '',
    requireLetter: Boolean(rule.requireLetter),
    requireNumber: Boolean(rule.requireNumber),
    errorMessage: rule.errorMessage || '',
  };
}

/** Editable draft -> the raw rule, before normalizeFreeTextValidation tidies it. */
export function freeTextDraftToRawValidation(draft) {
  const d = { ...EMPTY_FREE_TEXT_VALIDATION_DRAFT, ...(draft || {}) };
  const raw = {
    format: d.format,
    allowDecimals: d.allowDecimals,
    maxDecimalPlaces: d.maxDecimalPlaces,
    allowSpaces: d.allowSpaces,
    allowedCharacters: d.allowedCharacters,
    minLength: d.minLength,
    requireLetter: d.requireLetter,
    requireNumber: d.requireNumber,
    errorMessage: d.errorMessage,
  };
  if (d.minMode !== 'none' && String(d.minValue).trim() !== '') {
    raw.min = d.minValue;
    raw.minInclusive = d.minMode === 'gte';
  }
  if (d.maxMode !== 'none' && String(d.maxValue).trim() !== '') {
    raw.max = d.maxValue;
    raw.maxInclusive = d.maxMode === 'lte';
  }
  return raw;
}

/**
 * The draft's rule as it will be stored, plus anything the builder has to fix
 * first: contradictions, and limits or lengths that were typed but are not a
 * usable number (which the rule would otherwise quietly leave out).
 */
export function resolveFreeTextValidationDraft(draft, maxLength) {
  const d = { ...EMPTY_FREE_TEXT_VALIDATION_DRAFT, ...(draft || {}) };
  const { rule, problems } = normalizeFreeTextValidation(freeTextDraftToRawValidation(d), maxLength);
  const typed = [...problems];
  if (d.format === 'number') {
    if (d.minMode !== 'none' && String(d.minValue).trim() === '') typed.unshift('Enter the minimum value, or choose "No minimum".');
    else if (d.minMode !== 'none' && !(rule && 'min' in rule)) typed.unshift('The minimum value must be a number.');
    if (d.maxMode !== 'none' && String(d.maxValue).trim() === '') typed.unshift('Enter the maximum value, or choose "No maximum".');
    else if (d.maxMode !== 'none' && !(rule && 'max' in rule)) typed.unshift('The maximum value must be a number.');
    if (d.allowDecimals && String(d.maxDecimalPlaces).trim() !== '' && !(rule && rule.maxDecimalPlaces)) {
      typed.unshift('Maximum decimal places must be a whole number from 1 to 10.');
    }
  } else if (String(d.minLength).trim() !== '' && !(rule && rule.minLength)) {
    typed.unshift('Minimum length must be a whole number of 1 or more.');
  }
  return { rule, problems: typed };
}

const inputClass =
  'w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-900 focus:ring-2 focus:ring-gray-500';

function Checkbox({ checked, onChange, children }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer text-sm">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="rounded" />
      <span>{children}</span>
    </label>
  );
}

export default function FreeTextValidationSettings({ draft, maxLength, onChange }) {
  const id = useId();
  const d = { ...EMPTY_FREE_TEXT_VALIDATION_DRAFT, ...(draft || {}) };
  const set = (patch) => onChange({ ...d, ...patch });
  const [sample, setSample] = useState('');

  const { rule, problems } = resolveFreeTextValidationDraft(d, maxLength);
  const defaultMessage = describeFreeTextValidation(rule ? { ...rule, errorMessage: undefined } : null);
  const shownMessage = describeFreeTextValidation(rule);
  const sampleError = rule && sample ? validateFreeTextAnswer(sample, rule) : null;

  const isNumber = d.format === 'number';
  const isCharacterFormat = CHARACTER_FORMATS.includes(d.format);

  return (
    <div className="space-y-3 pt-3 border-t border-gray-300" data-testid="free-text-validation-settings">
      <div className="text-xs font-semibold text-gray-700 uppercase flex items-center gap-2">
        <ShieldCheck className="w-4 h-4" />
        Validation Rules <span className="font-normal normal-case text-gray-500">(optional)</span>
      </div>

      <div>
        <label htmlFor={`${id}-format`} className="block text-sm font-medium mb-1">
          Participants may enter
        </label>
        <select
          id={`${id}-format`}
          value={d.format}
          onChange={(e) => set({ format: e.target.value })}
          className={inputClass}
        >
          {FORMAT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {isNumber && (
        <div className="space-y-3 p-3 rounded-lg border border-gray-300 bg-white/60 dark:bg-gray-900/40">
          <div className="flex flex-wrap items-center gap-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name={`${id}-kind`}
                checked={!d.allowDecimals}
                onChange={() => set({ allowDecimals: false })}
              />
              Whole numbers only
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name={`${id}-kind`}
                checked={d.allowDecimals}
                onChange={() => set({ allowDecimals: true })}
              />
              {/* Whole numbers pass too. "Decimals allowed" read as decimals required (FECOA-2101). */}
              Whole numbers or decimals
            </label>
            {d.allowDecimals && (
              <label className="flex items-center gap-2 text-sm">
                <span>Max decimal places</span>
                <input
                  type="text"
                  inputMode="numeric"
                  aria-label="Maximum decimal places"
                  placeholder="Any"
                  value={d.maxDecimalPlaces}
                  onChange={(e) => set({ maxDecimalPlaces: e.target.value })}
                  className="w-20 px-2 py-1.5 text-sm border rounded-lg bg-white dark:bg-gray-900"
                />
              </label>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="text-gray-600">Quick set:</span>
            <button
              type="button"
              onClick={() => set({ minMode: 'gt', minValue: '0' })}
              className="px-2 py-1 rounded border border-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
            >
              Positive numbers only (&gt; 0)
            </button>
            <button
              type="button"
              onClick={() => set({ minMode: 'gte', minValue: '0' })}
              className="px-2 py-1 rounded border border-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
            >
              0 or more (no negatives)
            </button>
          </div>

          {[
            {
              key: 'min',
              label: 'Minimum',
              mode: d.minMode,
              value: d.minValue,
              options: [
                ['none', 'No minimum'],
                ['gte', 'Greater than or equal to (≥)'],
                ['gt', 'Greater than (>)'],
              ],
              onMode: (minMode) => set({ minMode }),
              onValue: (minValue) => set({ minValue }),
            },
            {
              key: 'max',
              label: 'Maximum',
              mode: d.maxMode,
              value: d.maxValue,
              options: [
                ['none', 'No maximum'],
                ['lte', 'Less than or equal to (≤)'],
                ['lt', 'Less than (<)'],
              ],
              onMode: (maxMode) => set({ maxMode }),
              onValue: (maxValue) => set({ maxValue }),
            },
          ].map((limit) => (
            <div key={limit.key} className="flex flex-wrap items-center gap-2">
              <label htmlFor={`${id}-${limit.key}-mode`} className="w-20 text-sm font-medium">
                {limit.label}
              </label>
              <select
                id={`${id}-${limit.key}-mode`}
                value={limit.mode}
                onChange={(e) => limit.onMode(e.target.value)}
                className="w-48 px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-900 focus:ring-2 focus:ring-gray-500"
              >
                {limit.options.map(([value, text]) => (
                  <option key={value} value={value}>
                    {text}
                  </option>
                ))}
              </select>
              {limit.mode !== 'none' && (
                <input
                  type="text"
                  inputMode="decimal"
                  aria-label={`${limit.label} value`}
                  value={limit.value}
                  onChange={(e) => limit.onValue(e.target.value)}
                  className="w-28 px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-900"
                />
              )}
            </div>
          ))}
        </div>
      )}

      {isCharacterFormat && (
        <div className="grid grid-cols-2 gap-3 items-end">
          <Checkbox checked={d.allowSpaces} onChange={(allowSpaces) => set({ allowSpaces })}>
            Allow spaces
          </Checkbox>
          <div>
            <label htmlFor={`${id}-allowed`} className="block text-sm font-medium mb-1">
              Also allow these characters
            </label>
            <input
              id={`${id}-allowed`}
              type="text"
              placeholder="e.g. - . '"
              value={d.allowedCharacters}
              onChange={(e) => set({ allowedCharacters: e.target.value })}
              className={inputClass}
            />
          </div>
        </div>
      )}

      {!isNumber && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor={`${id}-min-length`} className="block text-sm font-medium mb-1">
              Minimum length
            </label>
            <input
              id={`${id}-min-length`}
              type="text"
              inputMode="numeric"
              placeholder="None"
              value={d.minLength}
              onChange={(e) => set({ minLength: e.target.value })}
              className={inputClass}
            />
          </div>
          <div className="text-xs text-gray-600 self-end pb-2">
            Maximum length is set above ({maxLength ?? 500} characters).
          </div>
        </div>
      )}

      {REQUIRED_CHARACTER_FORMATS.includes(d.format) && (
        <div className="flex flex-wrap gap-4">
          <Checkbox checked={d.requireLetter} onChange={(requireLetter) => set({ requireLetter })}>
            Must include at least one letter
          </Checkbox>
          <Checkbox checked={d.requireNumber} onChange={(requireNumber) => set({ requireNumber })}>
            Must include at least one number
          </Checkbox>
        </div>
      )}

      {rule && (
        <div>
          <label htmlFor={`${id}-message`} className="block text-sm font-medium mb-1">
            Error message <span className="font-normal text-gray-500">(optional)</span>
          </label>
          <input
            id={`${id}-message`}
            type="text"
            maxLength={300}
            placeholder={defaultMessage || ''}
            value={d.errorMessage}
            onChange={(e) => set({ errorMessage: e.target.value })}
            className={inputClass}
          />
        </div>
      )}

      {problems.length > 0 ? (
        <p className="text-sm text-red-600" role="alert" data-testid="free-text-validation-problem">
          {problems[0]}
        </p>
      ) : rule ? (
        <div className="space-y-2 text-sm">
          <p className="text-gray-700">
            Participants who enter something else will see:{' '}
            <span className="font-medium">“{shownMessage}”</span>
          </p>
          <div className="flex items-center gap-2">
            <input
              type="text"
              aria-label="Try an answer"
              placeholder="Try an answer"
              value={sample}
              onChange={(e) => setSample(e.target.value)}
              className="flex-1 px-3 py-1.5 text-sm border rounded-lg bg-white dark:bg-gray-900"
            />
            {sample.trim() !== '' &&
              (sampleError ? (
                <span className="flex items-center gap-1 text-red-600" data-testid="free-text-validation-sample">
                  <XCircle className="w-4 h-4" /> Not accepted
                </span>
              ) : (
                <span className="flex items-center gap-1 text-green-700" data-testid="free-text-validation-sample">
                  <CheckCircle2 className="w-4 h-4" /> Accepted
                </span>
              ))}
          </div>
        </div>
      ) : (
        <p className="text-xs text-gray-600">No rule: participants can enter any text, as before.</p>
      )}
    </div>
  );
}
